import { useState, useContext } from "react";
import NavigationContext from "../context/NavigationContext";
import AuthCC from "../api/AuthCommunicationController";
import Status from "../models/Status";
import { logger } from "../utils/logger";

export function useLoginViewModel() {
    const [status, setStatus] = useState(Status.IDLE);
    const [error, setError] = useState<string | null>(null);
    const { onNavigate } = useContext(NavigationContext);

    const login = async (email: string, password: string) => {
        setStatus(Status.LOADING);
        try {
            await AuthCC.login(email, password);
            setError(null);
            setStatus(Status.IDLE);

            // Notifica che la sessione è cambiata
            const customEvent = new CustomEvent("userLoggedIn");
            window.dispatchEvent(customEvent);

            // Recupera la sessione per determinare il tipo di utente
            const sessionString = localStorage.getItem("ApolloSession");
            if (sessionString) {
                const sessions = JSON.parse(sessionString);
                const activeSession = Array.isArray(sessions) ? sessions.find(obj => obj.active) : null;

                // Log per debug
                logger.log("Sessione attiva dopo login:", activeSession);

                if (activeSession && activeSession.type) {
                    // Naviga in base al tipo di utente
                    onNavigate("/");
                } else {
                    logger.error("Nessuna sessione attiva o tipo utente mancante dopo il login");
                    setError("Errore durante l'accesso");
                    setStatus(Status.ERROR);
                }
            }
        } catch (err) {
            if (err instanceof Error) {
                if (err.message === "Network Error") setError("Errore di rete");
                else setError("Username o password errati");
            } else {
                setError("Username o password errati");
            }
            setStatus(Status.ERROR);
        }
    };

    const logout = async () => {
        try {
            await AuthCC.logout(); // Chiamata API per il logout
        } catch (err) {
            logger.error("Errore durante il logout:", err);
        }
        localStorage.removeItem("ApolloSession"); // Rimuove la sessione salvata
        const customEvent = new CustomEvent("userLoggedOut");
        window.dispatchEvent(customEvent);

        onNavigate("/login"); // Reindirizza alla pagina di login
    };

    const loginWithSessionId = async (sessionId: string) => {
        setStatus(Status.LOADING);
        try {
            const user = await AuthCC.loginWithSessionId(sessionId);

            // Naviga solo se abbiamo un utente valido
            if (user) {
                logger.log("Login SSO riuscito, sessione aggiornata:", localStorage.getItem("ApolloSession"));
                window.dispatchEvent(new CustomEvent("userLoggedIn"));
                onNavigate("/");
            } else {
                logger.error("Login SSO riuscito ma utente non valido");
                setError("Errore durante l'autenticazione");
                setStatus(Status.ERROR);
                return;
            }

            setError(null);
            setStatus(Status.IDLE);
        } catch (e) {
            logger.error("Errore SSO:", e);
            setError("Autenticazione SSO fallita");
            setStatus(Status.ERROR);
            onNavigate("/login");
        }
    };

    return {
        status,
        error,
        login,
        logout,
        loginWithSessionId
    };
}
